// Dumb card — no store, no SQL. A titled card with one proportional 100 % bar (a row of
// `v-sparkline` bar segments, one per item, each as wide as its share) and a two-column legend of
// the items with a percentage chip; hovering or focusing a legend item dims the other segments.
// Moved from issue-analysis (`components/cards/IssueStatusBarCard.vue`, its Overview "Issue Status
// Breakdown") on 2026-10-12 under the rule "an app's src/components/ holds only pages" (PLATFORM.md
// §3.2 rule 9) — renamed because nothing in it is issue-specific: it renders any
// `[{ id, title, color, value }]` share list; the title is the page's. Its query stays app-side
// (issue-analysis: `src/composables/useIssueStatusBreakdown.js`).
//
//   props:  title    (String)   the card's heading, already translated
//           items    (Array)    [{ id, title, color, value }] — `value` is the share in percent
//                               (they should add up to 100); `id` keys the legend hover (a
//                               missing id dims nothing); items with value <= 0 are not drawn
//           loading  (Boolean)  true → a card skeleton instead of the card
//   emits:  none (but `select`, below)
//
// Two OPT-IN props (AM_ABSORPTION_PLAN step C2 — standalone asset-management's
// AssetStatusBarCard, click-to-filter on a status, folded in here instead of a second card). Both
// default off, and off they change nothing: the element tree, its props and listeners are the ones
// this card rendered before them (issue-analysis's Overview card passes neither).
//   selectable  (Boolean) a segment or a legend item is a toggle: a click (on a legend item also
//               Enter/Space — Vuetify's own key handling of a clickable list item) emits `select`.
//               The card gets the namespaced class `rdrive-status-breakdown-card--selectable`
//               (pointer + opacity transition on the segments); each legend item is a clickable
//               `v-list-item` with role="option" and aria-selected (the legend is Vuetify's
//               role="listbox"), `active` while selected. Dimming turns into standalone AM's:
//               OPACITY (`@rdrive/theme` `chart.dimOpacity`) instead of the dimmed colour — the
//               hovered item first, else the selection. false → no listener, class or attribute.
//   selectedId  (String|Number|null) read only while `selectable`: the parent-owned selection —
//               the item with this `id` is the active legend item and every other segment dims.
//               The card never sets it.
//   emits:  select  (item)  only while `selectable`: the clicked item, as given in `items` (its
//                           `id` and `title` — what a cross-filter and its label need); the parent
//                           toggles its selection and passes `selectedId` back
//
// One more OPT-IN (2026-10-16, PNG export on every chart; off = nothing rendered changes):
//   exportFilename (String) a download button at the right of the title row that saves the card as
//                  a PNG under this name — the card AS IT LOOKS on screen (title, the segmented bar,
//                  the legend tiles with their percentage chips), redrawn from its live geometry in
//                  the theme's colours by ../../helpers/statusBreakdownPng.js; every status in full
//                  colour (the hover/selection dimming is not exported). '' = no button.
//   exportTitle    (String) the PNG's title; '' = `title`
//   setup:  downloadPng()  the button's action, callable through a template ref
//
// Colours: each item's own `color`; the dimmed segments are @rdrive/theme's
// `chart.statusBreakdown.dimmed` (or, selectable, `chart.dimOpacity`). Full contract:
// ../../../README.md. The logic lives in ./setup.js.
import setup from './setup.js';

export default {
  name: 'status-breakdown-card',
  props: {
    title: {
      type: String,
      default: ''
    },
    // [{ id, title, color, value }] — `value` is the item's share in percent
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: true
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selectedId: {
      type: [String, Number],
      default: null
    },
    // Opt-in PNG export (see above); '' = no button
    exportFilename: {
      type: String,
      default: ''
    },
    // The PNG's title; '' = `title`
    exportTitle: {
      type: String,
      default: ''
    }
  },
  emits: ['select'],
  setup,
};
